/**
 * TeamsList — workspace Teams (Admin/HR + managers). Group members so their
 * combined timesheet is filterable. Create/edit with name, logo, color, members.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from '../../utils/toast';
import { Plus, Users, X, Trash2, Pencil } from 'lucide-react';
import { wsApi, assetUrl } from './wsApi';
import { T, PrimaryButton, IconAction, ConfirmDialog } from '../../components/em/ui';
import { LogoPicker, MemberCombobox, ColorSwatchSelect, SkeletonGrid, hoverLift, hoverLiftStyle } from './wsUi';

function TeamModal({ initial, users, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [name, setName] = useState(initial?.name || '');
  const [logo, setLogo] = useState(initial?.logo || '');
  const [color, setColor] = useState(initial?.color || '#f54b01');
  const [memberIds, setMemberIds] = useState(
    (initial?.members || []).map((m) => Number(m.user_id)),
  );
  const [saving, setSaving] = useState(false);

  const toggle = (id) =>
    setMemberIds((s) => (s.includes(Number(id)) ? s.filter((x) => x !== Number(id)) : [...s, Number(id)]));

  const save = async () => {
    if (!name.trim()) return toast.error('Team name is required');
    setSaving(true);
    try {
      const body = { name: name.trim(), logo, color, member_ids: memberIds };
      if (editing) await wsApi.updateTeam(initial.id, body);
      else await wsApi.createTeam(body);
      toast.success(editing ? 'Team updated' : 'Team created');
      onSaved();
    } catch {
      toast.error('Could not save team');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{editing ? 'Edit Team' : 'New Team'}</h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={fieldLbl}>Logo
            <div style={{ marginTop: 6 }}>
              <LogoPicker value={logo} onChange={setLogo} color={color} label={name} />
            </div>
          </label>

          <label style={fieldLbl}>Team Name *
            <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Design Pod" />
          </label>

          <div style={fieldLbl}>Colour
            <div style={{ marginTop: 6 }}>
              <ColorSwatchSelect value={color} onChange={setColor} />
            </div>
          </div>

          <div style={fieldLbl}>Members ({memberIds.length})
            <div style={{ marginTop: 6 }}>
              <MemberCombobox users={users} selected={memberIds} onToggle={toggle} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={primaryBtn}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Team'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TeamsList() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {} new | team edit | null

  const load = useCallback(() => {
    setLoading(true);
    wsApi.listTeams().then(setTeams)
      .catch(() => toast.error('Could not load teams'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { wsApi.users().then(setUsers).catch(() => {}); }, []);

  const openEdit = async (id) => {
    try { setModal(await wsApi.getTeam(id)); }
    catch { toast.error('Could not open team'); }
  };

  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const del = async () => {
    if (!confirming) return;
    setDeleting(true);
    try {
      await wsApi.deleteTeam(confirming.id);
      setTeams((t) => t.filter((x) => x.id !== confirming.id));
      toast.success('Team deleted');
      setConfirming(null);
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
        <PrimaryButton icon={Plus} onClick={() => setModal({})}>New Team</PrimaryButton>
      </div>

      {loading ? (
        <SkeletonGrid count={4} minWidth={280} height={90} />
      ) : teams.length === 0 ? (
        <div style={{ background: 'var(--em-bg-card)', border: `1px dashed ${T.border}`, borderRadius: 14, padding: 50, textAlign: 'center' }}>
          <Users size={42} color={T.fg7} style={{ marginBottom: 12, opacity: 0.6 }} />
          <p style={{ font: `600 15px ${T.font}`, color: T.fg1, margin: '0 0 14px' }}>No teams yet.</p>
          <div style={{ display: 'inline-flex' }}><PrimaryButton icon={Plus} onClick={() => setModal({})}>Create your first team</PrimaryButton></div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {teams.map((t) => (
            <div key={t.id} onClick={() => openEdit(t.id)} {...hoverLift}
              style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, cursor: 'pointer', boxShadow: T.shadowSm, ...hoverLiftStyle }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 11, background: t.logo ? '#fff' : (t.color || T.orange), color: '#fff', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 16px ${T.font}`, flexShrink: 0 }}>
                  {t.logo
                    ? <img src={assetUrl(t.logo)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (t.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: 0, font: `700 14px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</h3>
                  <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>{t.member_count} member{t.member_count !== 1 ? 's' : ''}</div>
                </div>
                <IconAction icon={Pencil} label="Edit" onClick={(e) => { e.stopPropagation(); openEdit(t.id); }} />
                <IconAction icon={Trash2} label="Delete" onClick={(e) => { e.stopPropagation(); setConfirming(t); }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <TeamModal initial={modal.id ? modal : null} users={users} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      <ConfirmDialog
        open={!!confirming}
        title="Delete team?"
        message={`"${confirming?.name}" will be removed. Its members keep their accounts and any other teams.`}
        confirmLabel="Delete team"
        danger
        busy={deleting}
        onConfirm={del}
        onCancel={() => !deleting && setConfirming(null)}
      />
    </div>
  );
}

const fieldLbl = { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)' };
const input = { width: '100%', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 11px', fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginTop: 6, background: 'var(--em-bg-card)', color: 'var(--em-fg1)' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modal = { background: 'var(--em-bg-card)', borderRadius: 14, padding: '22px 24px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' };
const modalHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 7, background: '#f54b01', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--em-bg-card)', color: 'var(--em-fg2)', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
